import { useEffect, useState } from 'react';

import config from '@/config';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';

import InlinePostImage from './InlinePostImage';

import './BoardBodyPicture.css';

export default function InlineImageCarousel({
  groupId,
  images,
  onRemoveImage,
  onCaptionChange,
  previewMode = false
}) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [slides, setSlides] = useState(images);

  // ——— PRELOAD ALL SLIDE IMAGES (for faster loading) ———
  useEffect(() => {
    images.forEach(img => {
      const pre = new Image();
      pre.src = `${config.app.imageServiceDomain}/${img.image_id}/w=${1200}`; // match your max
    });
  }, [images]);

  // Always call hooks at the top
  useEffect(() => {
    if (activeSlide >= images.length) {
      setActiveSlide(Math.max(0, images.length - 1));
    }
  }, [activeSlide, images.length]);

  // If images array is empty, render nothing
  if (!slides || slides.length === 0) {
    return null;
  }

  // Clamp activeSlide for safe indexing
  const safeIndex = Math.min(activeSlide, images.length - 1);

  const current = slides[safeIndex];

  const currentImage = {
    image_id: current.image_id,
    picture_description: current.picture_description || '',
    pictureId: current.pictureId || 0,
  };

  // Navigation handlers
  const handlePrev = () => {
    setActiveSlide(idx => Math.max(0, idx - 1));
  };
  const handleNext = () => {
    setActiveSlide(idx => Math.min(images.length - 1, idx + 1));
  };

  // Slider controls
  const sliderContainer = images.length > 1 && (
    <div
      className="artwork-view-pictures-slider__control-indicators-container"
      contentEditable={false}
    >
      <div className="artwork-view-pictures-slider__control-outter prev">
        <PrevButton
          onClick={e => {
            e.stopPropagation();
            handlePrev();
          }}
          className={`artwork-view-pictures-slider__control ${
            safeIndex === 0 ? 'disabled' : ''
          }`}
        />
      </div>
      <div className="artwork-view-pictures-section__slide-indicators">
        {slides.map((img, idx) => (
          <div
            key={img.image_id}
            className={`artwork-view-pictures-section__slide-indicator${
              safeIndex === idx ? ' active' : ''
            }`}
          />
        ))}
      </div>
      <div className="artwork-view-pictures-slider__control-outter next">
        <NextButton
          onClick={e => {
            e.stopPropagation();
            handleNext();
          }}
          className={`artwork-view-pictures-slider__control ${
            safeIndex === slides.length - 1 ? 'disabled' : ''
          }`}
        />
      </div>
    </div>
  );

  const updateCaption = (groupId, pictureId, newCaption) => {
    setSlides(prevSlides =>
      prevSlides.map(slide => {
        if (slide.pictureId === pictureId) {
          return { ...slide, picture_description: newCaption };
        }
        return slide;
      })
    );
    onCaptionChange(groupId, pictureId, newCaption);
  };

  const removeSlide = (pictureId) => {
    setSlides(prevSlides => prevSlides.filter(slide => slide.pictureId !== pictureId));
    onRemoveImage(groupId, pictureId);
  };

  return (
    <div className="inline-image-carousel">
      <div className="inline-image-carousel__image-container">
        <InlinePostImage
          groupId={groupId}
          key={currentImage.pictureId}
          image={currentImage}
          onRemove={() => removeSlide(currentImage.pictureId)}
          onCaptionChange={updateCaption}
          previewMode={previewMode}
          size={1200}
          sliderContainer={sliderContainer}
        />
      </div>
      {/* bottom-of-carousel copy – one per carousel, never re-mounted */}
      {images.length > 1 && (
        <div className="inline-image-carousel__controls">
          {sliderContainer}
        </div>
      )}
    </div>
  );
}
